import { Text, VStack } from "@chakra-ui/react"
import { AUGMENTATION_PANEL_INPUT_PROPS } from "@pathwright/ux/src/components/augmentation/augmentationPanelStyles"
import { AugmentationComponent } from "@pathwright/ux/src/components/augmentation/AugmentationToolbar"
import { toaster } from "@pathwright/ux/src/components/chakra/ui/toaster"
import Field from "@pathwright/ux/src/components/form/Field"
import { FormikWithDiscardFormState } from "@pathwright/ux/src/components/form/state/FormikDiscardFormState"
import KeyboardFormSubmit from "@pathwright/ux/src/components/form/state/KeyboardFormSubmit"
import { Form, FormikProps } from "formik"
import { useMemo } from "react"
import { useHistory, useLocation } from "react-router-dom"
import { z } from "zod"
import { toFormikValidate } from "zod-formik-adapter"
import {
  CoverPageCategoryFragment as CoverPageCategory,
  CoverPageCategoryFragmentDoc,
  useCreateCoverPageCategoryMutation,
  useUpdateCoverPageCategoryMutation
} from "@pathwright/graphql-client/src/generated"
import { useCoverPageIsEditable } from "../access"
import { useCoverPageContext } from "../CoverPageProvider"
import { COVER_PAGE_PANEL_ROOT_KEY } from "../constants"
import { getCategoryPositioningContext } from "../positioning"
import { useCoverPageTranslate } from "../utils"
import { CoverPagePanelFormActions } from "./CoverPagePanelFormActions"
import { CoverPagePanelRichTextField } from "./CoverPagePanelRichTextField"
import { useCoverPagePanelErrorMessage } from "./utils"

const categorySchema = z.object({
  name: z.string(),
  description: z.string()
})

type CoverPagePanelCategoryValues = z.infer<typeof categorySchema>

function useCategoryCreateName() {
  const location = useLocation()

  return useMemo(() => {
    return new URLSearchParams(location.search).get("name")?.trim() || ""
  }, [location.search])
}

function buildInitialValues(
  category: CoverPageCategory | null,
  initialName = ""
): CoverPagePanelCategoryValues {
  return {
    name: category?.name || initialName,
    description: category?.description || ""
  }
}

function getCategoryMutationVariables(values: CoverPagePanelCategoryValues) {
  return {
    name: values.name.trim(),
    description: values.description || ""
  }
}

export function preserveCategoryPosition<
  T extends Pick<CoverPageCategory, "position">
>(updatedCategory: T, currentCategory: CoverPageCategory | null): T {
  if (updatedCategory.position || !currentCategory?.position) {
    return updatedCategory
  }

  return {
    ...updatedCategory,
    position: currentCategory.position
  }
}

function CoverPagePanelCategoryForm({
  category
}: {
  category: CoverPageCategory | null
}) {
  const { t } = useCoverPageTranslate()
  const isEditable = useCoverPageIsEditable()
  const getErrorMessage = useCoverPagePanelErrorMessage()
  const history = useHistory()
  const resourceId = useCoverPageContext((value) => value.resourceId)
  const categories = useCoverPageContext((value) => value.categories)
  const updateCoverPageResourceDetails = useCoverPageContext(
    (value) => value.updateCoverPageResourceDetails
  )
  const refetchCoverPageResource = useCoverPageContext(
    (value) => value.refetchCoverPageResource
  )

  const initialName = useCategoryCreateName()

  const [createCategoryMutation] = useCreateCoverPageCategoryMutation()
  const [updateCategoryMutation] = useUpdateCoverPageCategoryMutation()
  const initialValues = useMemo(
    () => buildInitialValues(category, initialName),
    [category, initialName]
  )
  const isCreate = !category
  const formStateKey = useMemo(
    () => ["cover-page", "category", category?.id || "new", resourceId],
    [category?.id, resourceId]
  )

  const validate = useMemo(
    () =>
      toFormikValidate(
        categorySchema.extend({
          name: z.string().refine(
            (value) => value.trim().length > 0,
            t("panel_categories.name_required", {
              defaultValue: "Enter a category name."
            })
          )
        })
      ),
    [t]
  )

  async function handleSubmit(values: CoverPagePanelCategoryValues) {
    try {
      if (!isEditable)
        throw new Error("You do not have permission to edit this Cover Page.")
      const variables = getCategoryMutationVariables(values)
      const categoryPositioningContext =
        getCategoryPositioningContext(resourceId)
      const result = isCreate
        ? await createCategoryMutation({
            variables: {
              ...variables,
              categoryPositioningContext,
              order: 0
            }
          })
        : await updateCategoryMutation({
            variables: {
              ...variables,
              categoryPositioningContext,
              id: category!.id
            },
            update: (cache, { data }) => {
              const updatedCategory = data?.updateCategory
              const cacheId = updatedCategory
                ? cache.identify(updatedCategory)
                : null

              if (!updatedCategory || !cacheId) return

              cache.writeFragment({
                id: cacheId,
                fragment: CoverPageCategoryFragmentDoc,
                fragmentName: "CoverPageCategory",
                variables: {
                  categoryPositioningContext
                },
                data: preserveCategoryPosition(updatedCategory, category)
              })
            }
          })
      const savedCategory =
        result.data && "createCategory" in result.data
          ? result.data.createCategory
          : result.data && "updateCategory" in result.data
          ? result.data.updateCategory
          : null
      const categoryId = savedCategory?.id || category?.id

      if (!categoryId) {
        throw new Error(
          t("panel_categories.unable_to_save_category", {
            defaultValue: "Unable to save category."
          })
        )
      }

      if (isCreate) {
        await updateCoverPageResourceDetails({
          categories: Array.from(
            new Set(
              categories
                .map((currentCategory) => currentCategory.id)
                .concat(categoryId)
            )
          )
        })
      }

      await refetchCoverPageResource()

      if (isCreate) {
        history.push(`/${COVER_PAGE_PANEL_ROOT_KEY}`)
      }
    } catch (error) {
      toaster.create({
        title: getErrorMessage(
          error,
          isCreate
            ? t("panel_errors.create_category", {
                defaultValue: "Error creating Cover Page's category:"
              })
            : t("panel_errors.update_category", {
                defaultValue: "Error updating Cover Page's category:"
              })
        ),
        type: "error",
        duration: 4000,
        closable: true
      })
      throw error
    }
  }

  return (
    <FormikWithDiscardFormState
      formStateKey={formStateKey}
      initialValues={initialValues}
      validate={validate}
      validateOnMount
      enableReinitialize
      formStateOnSubmit={isCreate ? "clearOnSuccess" : "preserve"}
      onSubmit={handleSubmit}
    >
      {(form: FormikProps<CoverPagePanelCategoryValues>) => {
        const formId = "cover-page-panel-category"

        return (
          <Form
            id={formId}
            style={{
              display: "flex",
              flexDirection: "column",
              width: "100%"
            }}
          >
            <KeyboardFormSubmit />
            <VStack align="stretch" gap="15px" w="full" pt="15px">
              <Field
                {...AUGMENTATION_PANEL_INPUT_PROPS}
                name="name"
                label={t("panel_categories.category_name", {
                  defaultValue: "Category name"
                })}
                placeholder={t("panel_categories.category_name_placeholder", {
                  defaultValue: "Enter a category name"
                })}
              />
              <CoverPagePanelRichTextField<CoverPagePanelCategoryValues>
                name="description"
                label={t("panel_categories.category_description", {
                  defaultValue: "Category description"
                })}
                placeholder={t(
                  "panel_categories.category_description_placeholder",
                  {
                    defaultValue: "About the category..."
                  }
                )}
                disabled={!isEditable || form.isSubmitting}
              />
            </VStack>
            <CoverPagePanelFormActions
              disabled={!isEditable}
              form={form}
              formId={formId}
              requireDirty={!isCreate}
              submit={{
                children: isCreate
                  ? t("panel_categories.create_category", {
                      defaultValue: "Create category"
                    })
                  : t("panel.apply_changes", {
                      defaultValue: "Apply changes"
                    })
              }}
              discard={{
                children: isCreate
                  ? t("panel.discard", { defaultValue: "Discard" })
                  : t("panel.discard_changes", {
                      defaultValue: "Discard changes"
                    })
              }}
            />
          </Form>
        )
      }}
    </FormikWithDiscardFormState>
  )
}

export function CoverPagePanelCategoryCreateRoute() {
  return <CoverPagePanelCategoryForm category={null} />
}

export const CoverPagePanelCategoryRoute: AugmentationComponent = ({
  route
}) => {
  const { t } = useCoverPageTranslate()
  const categories = useCoverPageContext((value) => value.categories)
  const categoryId = Number(
    (route?.match?.params as { categoryId?: string })?.categoryId || ""
  )
  const category =
    categories.find((category) => category.id === categoryId) || null

  if (!category) {
    return (
      <Text fontSize="sm" color="blackAlpha.700" m={0}>
        {t("panel_categories.category_not_found", {
          defaultValue: "Category not found."
        })}
      </Text>
    )
  }

  return <CoverPagePanelCategoryForm category={category} />
}
